import { Center, Flex, Group, Stack } from '@mantine/core';
import { useApiGetScoreHistoryForWorkspace } from '@repo/api-codegen';
import { Text } from '@repo/foundations';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { DataQualityProgressIcon } from '../../../../../../../components/DataQualityScore/DataQualityProgressIcon';
import { QualityScorePercentage } from '../../../../../../../components/DataQualityScore/QualityScoreRing/QualityScorePercentage';
import { QualityScoreRing } from '../../../../../../../components/DataQualityScore/QualityScoreRing/QualityScoreRing';
import { QualityScoreRingLoading } from '../../../../../../../components/DataQualityScore/QualityScoreRing/QualityScoreRingLoading';
import { WidgetSize } from '../../../../../../../interfaces';
import { useHomepageTeam } from '../../../../../hooks/useHomepageTeam';
import type { IWidgetItemProps } from '../../../types';

interface PolicyBreakdown {
	policy_id: string;
	policy_name: string;
	percentage: number;
	score: number;
	possible: number;
}

export function DataQualityScoreWidgetV2({
	widget,
	href,
}: IWidgetItemProps & { href: string }) {
	const { isTeamsPage, team } = useHomepageTeam();

	const scoreHistoryQueryParams = useMemo(() => {
		const params: Record<string, string> = {};

		// If tag filter is set, use it (consolidates across teams)
		// Otherwise, use team filter if on team page
		if (widget.preferences?.tag_id) {
			params.tag_id = widget.preferences.tag_id;
		} else if (isTeamsPage && team?.id) {
			params.team_id = team.id;
		}

		return params;
	}, [isTeamsPage, team?.id, widget.preferences?.tag_id]);

	const { data: scoreHistory, isLoading } = useApiGetScoreHistoryForWorkspace(
		{
			queryParams: scoreHistoryQueryParams,
		},
		{
			enabled: !isTeamsPage || !!team?.id,
		}
	);

	const qualityData = useMemo(() => {
		if (!scoreHistory?.points?.length) {
			return null;
		}

		const filteredPoints = scoreHistory.points.filter((point) => {
			const hasPossibleScore = point.total_possible > 0;
			const hasPolicyBreakdown =
				Object.keys(point.policy_breakdown ?? {}).length > 0;
			return hasPossibleScore || hasPolicyBreakdown;
		});

		if (!filteredPoints.length) {
			return null;
		}

		const latestPoint = filteredPoints[filteredPoints.length - 1];
		const overallPercentage =
			latestPoint.total_possible > 0
				? (latestPoint.total_score / latestPoint.total_possible) * 100
				: 0;

		const policyBreakdowns: PolicyBreakdown[] = Object.values(
			latestPoint.policy_breakdown ?? {}
		).map((policyData) => ({
			policy_id: policyData.policy_id,
			policy_name: policyData.policy_name,
			percentage: policyData.percentage,
			score: policyData.score,
			possible: policyData.possible,
		}));

		return {
			percentage: overallPercentage,
			policies: policyBreakdowns,
		};
	}, [scoreHistory]);

	const direction =
		widget.size === WidgetSize.MEDIUM ? 'vertical' : 'horizontal';

	return (
		<Stack>
			<Link to={href} style={{ textDecoration: 'none' }}>
				<Flex
					direction={direction === 'vertical' ? 'column' : 'row'}
					gap="md"
					p="sm"
					h={296}
					align="center"
					justify={direction === 'vertical' ? 'space-between' : 'space-evenly'}
				>
					<Center w={direction === 'vertical' ? undefined : '50%'}>
						{isLoading && (
							<QualityScoreRing
								score={0}
								size={direction === 'vertical' ? 'md' : 'lg'}
							/>
						)}
						{!isLoading && (
							<QualityScoreRing
								score={qualityData?.percentage ?? 0}
								size={direction === 'vertical' ? 'md' : 'lg'}
							/>
						)}
					</Center>
					<Stack
						spacing={direction === 'vertical' ? 'xs' : 'md'}
						w={direction === 'vertical' ? '100%' : '50%'}
					>
						{isLoading && <QualityScoreRingLoading size="xs" />}
						{!isLoading &&
							qualityData?.policies.map((policy) => (
								<Group key={policy.policy_id} position="apart" spacing={0}>
									<Group spacing="xs">
										<DataQualityProgressIcon score={policy.percentage} />
										<Text
											size="sm"
											fw={500}
											style={{
												overflow: 'hidden',
												textOverflow: 'ellipsis',
												whiteSpace: 'nowrap',
											}}
										>
											{policy.policy_name}
										</Text>
									</Group>
									<Text size="sm" color="text/secondary/default">
										<QualityScorePercentage
											size="2xs"
											score={policy.percentage}
											displayBadge={false}
										/>
									</Text>
								</Group>
							))}
						{!isLoading &&
							(!qualityData || qualityData.policies.length === 0) && (
								<Center w="100%" h="100%">
									<Text color="text/secondary/default" size="sm">
										No Data Quality Score Found
									</Text>
								</Center>
							)}
					</Stack>
				</Flex>
			</Link>
		</Stack>
	);
}
